/*
 * Help-desk assisted session — the my.trumin side of DESIGN-HELPDESK-KNOWN-
 * TICKET-ASSISTED-COMPLETION-V1 (§2.3, §2.4; LOCKED contract
 * HELPDESK-CONTRACT-LOCKED-1102). my-api routes: POST /assist/exchange,
 * GET /me/tickets/{id}/readiness and GET /assist/return-url, all with
 * `Authorization: Assist <as1 session>`.
 *
 * The session lives IN MEMORY ONLY (the caller keeps it in React state): this
 * module never touches localStorage, sessionStorage, cookies or IndexedDB, so
 * nothing survives on the shared help-desk tablet. The link token and the
 * session are never logged.
 */
import type { Readiness } from './myApi'
import { mapResolvedDataCollection, type DataCollectionResolved } from './participantConfig'

export interface AssistSession {
  session: string
  expiresAt: string
  ticketId: string
  eventId: string
}

/** A refused exchange or call: `code` is my-api's error code. */
export class AssistError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code)
  }
}

const join = (base: string, path: string) => `${base.replace(/\/$/, '')}${path}`

async function fail(res: Response): Promise<never> {
  let code = 'unknown'
  try {
    const b = (await res.json()) as { error?: unknown }
    if (typeof b?.error === 'string') code = b.error
  } catch {
    // keep 'unknown'
  }
  throw new AssistError(res.status, code)
}

/** Consume the single-use link once. 410 link_used / link_expired, 404, 409
 *  claim_mode and 503 surface as AssistError. */
export async function exchangeAssistToken(base: string, token: string, fetchImpl: typeof fetch = fetch): Promise<AssistSession> {
  const res = await fetchImpl(join(base, '/assist/exchange'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token }),
    referrerPolicy: 'no-referrer',
  })
  if (!res.ok) return fail(res)
  const b = (await res.json()) as { session: string; expires_at: string; ticket_id: string; event_id: string }
  return { session: b.session, expiresAt: b.expires_at, ticketId: b.ticket_id, eventId: b.event_id }
}

const assistHeaders = (s: AssistSession) => ({ Authorization: `Assist ${s.session}`, Accept: 'application/json' })

export async function fetchAssistReadiness(base: string, s: AssistSession, fetchImpl: typeof fetch = fetch): Promise<Readiness> {
  const res = await fetchImpl(join(base, `/me/tickets/${encodeURIComponent(s.ticketId)}/readiness`), { headers: assistHeaders(s) })
  if (!res.ok) return fail(res)
  return (await res.json()) as Readiness
}

/** End the session ("Return to the help desk"). url is the app's return deep
 *  link, or null while the scheme is not configured. */
export async function assistReturn(
  base: string,
  s: AssistSession,
  status: 'incomplete' | 'cancelled',
  fetchImpl: typeof fetch = fetch,
): Promise<{ url: string | null; status: 'complete' | 'incomplete' | 'cancelled' }> {
  const res = await fetchImpl(join(base, `/assist/return-url?status=${status}`), { headers: assistHeaders(s) })
  if (!res.ok) return fail(res)
  return (await res.json()) as { url: string | null; status: 'complete' | 'incomplete' | 'cancelled' }
}

/** The holder's identity core for the Get Ready profile step under assist
 *  (my-api GET /assist/identity: names from my-api, birthdate + gender via
 *  the audited AdminGetUser read). Never email or a phone VALUE: only
 *  `phonePresent` (contract addendum ~19:40Z Q2 — "Phone on file"). */
export interface AssistIdentity {
  givenName: string | null
  familyName: string | null
  birthdate: string | null
  gender: string | null
  /** The account HAS a phone (presence only; absent on the wire ⇒ false). */
  phonePresent: boolean
}

export async function fetchAssistIdentity(base: string, s: AssistSession, fetchImpl: typeof fetch = fetch): Promise<AssistIdentity> {
  const res = await fetchImpl(join(base, '/assist/identity'), { headers: assistHeaders(s) })
  if (!res.ok) return fail(res)
  const b = (await res.json()) as { given_name: string | null; family_name: string | null; birthdate: string | null; gender: string | null; phone_present?: unknown }
  return { givenName: b.given_name, familyName: b.family_name, birthdate: b.birthdate, gender: b.gender, phonePresent: b.phone_present === true }
}

/** The five attributes my-api may write under assist (locked addendum;
 *  phone_number E.164, never read back). */
export type AssistAttributeWrite = Partial<Record<'given_name' | 'family_name' | 'birthdate' | 'gender' | 'phone_number', string>>

export async function putAssistAttributes(base: string, s: AssistSession, attributes: AssistAttributeWrite, fetchImpl: typeof fetch = fetch): Promise<void> {
  const res = await fetchImpl(join(base, '/assist/attributes'), {
    method: 'PUT',
    headers: { ...assistHeaders(s), 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  })
  if (!res.ok) return fail(res)
}

/** 'open' while the session is unsettled; 'complete' once my-api's
 *  completion hook settled it. */
export async function fetchAssistStatus(base: string, s: AssistSession, fetchImpl: typeof fetch = fetch): Promise<string> {
  const res = await fetchImpl(join(base, '/assist/status'), { headers: assistHeaders(s) })
  if (!res.ok) return fail(res)
  return ((await res.json()) as { outcome: string }).outcome
}

/** The session EVENT's resolved participant-config data_collection (my-api
 *  GET /assist/events/{eventId}/participant-config; locked contract gap 2).
 *  Self-service reads this settings-direct with the Cognito token; under
 *  assist it comes through my-api, in the same wire shape, so the profile
 *  screens ask the event's real fields. Only the session's own event: any
 *  other id is refused by my-api (403 assist_scope), so it is never asked. */
export async function fetchAssistParticipantConfig(
  base: string,
  s: AssistSession,
  eventId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DataCollectionResolved> {
  if (eventId.toLowerCase() !== s.eventId.toLowerCase()) throw new AssistError(403, 'assist_scope')
  const res = await fetchImpl(join(base, `/assist/events/${encodeURIComponent(s.eventId)}/participant-config`), { headers: assistHeaders(s) })
  if (!res.ok) return fail(res)
  return mapResolvedDataCollection(eventId, (await res.json()) as Parameters<typeof mapResolvedDataCollection>[1])
}
